import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './AdminLogin.css';
import './AdminSignupOtpPage.css';

const PENDING_SIGNUP_KEY = 'signupOtp.pending';

const maskPhoneDisplay = (phone = '') => {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return phone;
  return `***-***-${digits.slice(-4)}`;
};

const readPendingSignup = () => {
  try {
    const raw = localStorage.getItem(PENDING_SIGNUP_KEY) || sessionStorage.getItem(PENDING_SIGNUP_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.provider || !parsed.email) return null;
    return parsed;
  } catch {
    return null;
  }
};

const persistPendingSignup = (payload) => {
  const serialized = JSON.stringify(payload);
  localStorage.setItem(PENDING_SIGNUP_KEY, serialized);
  sessionStorage.setItem(PENDING_SIGNUP_KEY, serialized);
};

const clearPendingSignup = () => {
  localStorage.removeItem(PENDING_SIGNUP_KEY);
  sessionStorage.removeItem(PENDING_SIGNUP_KEY);
};

const AdminSignupOtpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [pending, setPending] = useState(() => readPendingSignup());
  const [resolvingPending, setResolvingPending] = useState(false);

  const [method, setMethod] = useState('email');
  const [sessionId, setSessionId] = useState('');
  const [maskedTarget, setMaskedTarget] = useState('');
  const [codeDigits, setCodeDigits] = useState(Array(6).fill(''));
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [resendSeconds, setResendSeconds] = useState(0);

  const inputRefs = useRef([]);

  const canUseSms = useMemo(() => {
    const digits = String(pending?.phone || '').replace(/\D/g, '');
    return digits.length >= 8;
  }, [pending]);

  const consumeRedirectAfterLogin = () => {
    const redirect = localStorage.getItem('redirectAfterLogin');
    if (redirect) {
      localStorage.removeItem('redirectAfterLogin');
      return redirect;
    }
    return null;
  };

  const getPostSignupPath = () => {
    const loginRedirect = consumeRedirectAfterLogin();
    if (loginRedirect && loginRedirect.startsWith('/')) return loginRedirect;

    const pendingRedirect = String(pending?.redirectPath || '').trim();
    if (pendingRedirect.startsWith('/')) return pendingRedirect;

    return `/admin/onboarding${location.search || ''}`;
  };

  useEffect(() => {
    if (pending) return;

    let cancelled = false;
    const resolveFallbackPending = async () => {
      setResolvingPending(true);
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        const db = getFirestore();
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        if (!userSnap.exists()) return;

        const data = userSnap.data() || {};
        const otpVerified = data.signupOtpVerified;
        const otpRequired = data.signupOtpRequired === true || otpVerified === false;
        if (!otpRequired || otpVerified === true) return;

        const inferredProvider = data.authProvider ||
          (currentUser.providerData.some((provider) => provider.providerId === 'google.com') ? 'google' : 'email');
        const email = String(data.email || currentUser.email || '').trim().toLowerCase();
        if (!email) return;

        const fallback = {
          provider: inferredProvider,
          existingUser: true,
          uid: currentUser.uid,
          name: String(data.name || currentUser.displayName || email.split('@')[0] || 'User').trim(),
          email,
          phone: String(data.phone || '').trim(),
          consents: {
            smsEmail: data?.consents?.smsEmailConsent === true,
            privacyPolicy: data?.consents?.privacyPolicy === true,
            termsOfService: data?.consents?.termsOfService === true,
          },
          redirectPath: `/admin/onboarding${location.search || ''}`,
        };

        persistPendingSignup(fallback);
        if (!cancelled) {
          setPending(fallback);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Failed to restore pending OTP verification.');
        }
      } finally {
        if (!cancelled) {
          setResolvingPending(false);
        }
      }
    };

    resolveFallbackPending();
    return () => {
      cancelled = true;
    };
  }, [pending, location.search]);

  useEffect(() => {
    if (!pending && !resolvingPending) {
      navigate(`/admin/signup${location.search || ''}`, { replace: true });
      return;
    }
    if (!pending) return;
    // Default channel is EMAIL — the user may switch to SMS explicitly.
    setMethod('email');
  }, [pending, resolvingPending, canUseSms, navigate, location.search]);

  // NO auto-send: the code only leaves when the user taps "Send code".

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;
    const timer = setTimeout(() => setResendSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendSeconds]);

  const requestSignupOtp = httpsCallable(getFunctions(), 'requestSignupOtp');
  const verifySignupOtp = httpsCallable(getFunctions(), 'verifySignupOtp');

  const sendOtp = async (selectedMethod = method) => {
    if (!pending || sending || verifying) return;
    setError('');
    setSending(true);

    try {
      const res = await requestSignupOtp({
        method: selectedMethod,
        email: String(pending.email || '').trim().toLowerCase(),
        phone: String(pending.phone || '').trim(),
        name: String(pending.name || '').trim(),
      });

      const data = res.data || {};
      if (!data.sessionId) {
        throw new Error('Failed to create OTP session.');
      }

      setSessionId(data.sessionId);
      setMaskedTarget(data.maskedTarget || '');
      setMethod(selectedMethod);
      setCodeDigits(Array(6).fill(''));
      setResendSeconds(30);

      setTimeout(() => {
        if (inputRefs.current[0]) inputRefs.current[0].focus();
      }, 10);
    } catch (err) {
      const msg = err?.message || 'Failed to send OTP.';
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  const handleDigitChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...codeDigits];
    next[index] = digit;
    setCodeDigits(next);

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (!digit && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const completeSignup = async (otpMethod) => {
    if (!pending) throw new Error('Missing pending sign-up data.');

    const auth = getAuth();
    const db = getFirestore();
    const profileName = String(pending.name || pending.email?.split('@')?.[0] || 'User').trim();

    if (pending.existingUser === true) {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Session expired. Please sign in and try again.');
      }

      await setDoc(doc(db, 'users', currentUser.uid), {
        name: profileName,
        phone: String(pending.phone || '').trim(),
        email: String(pending.email || '').trim().toLowerCase(),
        signupOtpRequired: true,
        signupOtpVerified: true,
        signupOtpMethod: otpMethod,
        signupOtpVerifiedAt: new Date().toISOString(),
      }, { merge: true });
      return;
    }

    if (pending.provider === 'email') {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        String(pending.email).trim().toLowerCase(),
        String(pending.password || '')
      );

      await setDoc(doc(db, 'users', userCredential.user.uid), {
        name: profileName,
        phone: String(pending.phone || '').trim(),
        email: String(pending.email || '').trim().toLowerCase(),
        createdAt: new Date().toISOString(),
        authProvider: 'email',
        consents: {
          smsEmailConsent: !!pending.consents?.smsEmail,
          privacyPolicy: !!pending.consents?.privacyPolicy,
          termsOfService: !!pending.consents?.termsOfService,
          consentedAt: new Date().toISOString(),
        },
        signupOtpRequired: true,
        signupOtpVerified: true,
        signupOtpMethod: otpMethod,
        signupOtpVerifiedAt: new Date().toISOString(),
      });

    } else {
      const currentUser = auth.currentUser;
      if (!currentUser || currentUser.uid !== pending.uid) {
        throw new Error('Google session expired. Please sign up again.');
      }

      await setDoc(doc(db, 'users', pending.uid), {
        name: profileName,
        phone: String(pending.phone || '').trim(),
        email: String(pending.email || '').trim().toLowerCase(),
        createdAt: pending.createdAt || new Date().toISOString(),
        authProvider: 'google',
        consents: {
          smsEmailConsent: !!pending.consents?.smsEmail,
          privacyPolicy: !!pending.consents?.privacyPolicy,
          termsOfService: !!pending.consents?.termsOfService,
          consentedAt: new Date().toISOString(),
        },
        signupOtpRequired: true,
        signupOtpVerified: true,
        signupOtpMethod: otpMethod,
        signupOtpVerifiedAt: new Date().toISOString(),
      });
    }
  };

  const handleVerify = async () => {
    if (!sessionId) {
      setError('Please request an OTP code first.');
      return;
    }

    const code = codeDigits.join('');
    if (!/^\d{6}$/.test(code)) {
      setError('Please enter a valid 6-digit code.');
      return;
    }

    setError('');
    setVerifying(true);

    try {
      await verifySignupOtp({ sessionId, code });
      await completeSignup(method);
      clearPendingSignup();
      navigate(getPostSignupPath(), { replace: true });
    } catch (err) {
      const msg = err?.message || 'OTP verification failed.';
      setError(msg);
    } finally {
      setVerifying(false);
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !codeDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      inputRefs.current[index - 1]?.focus();
      return;
    }

    if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;

    event.preventDefault();
    const nextDigits = Array(6).fill('');
    for (let i = 0; i < pasted.length; i += 1) {
      nextDigits[i] = pasted[i];
    }
    setCodeDigits(nextDigits);

    const focusIndex = Math.min(pasted.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  if (resolvingPending && !pending) {
    return (
      <div className="admin-login-page">
        <div className="login-box">
          <div className="card card-primary card-outline">
            <div className="card-body login-card-body otp-card-body text-center">
              <div className="spinner-border text-warning" role="status" style={{ width: '2rem', height: '2rem' }}>
                <span className="sr-only">Loading...</span>
              </div>
              <p className="login-subheading" style={{ marginTop: '14px' }}>
                Restoring your pending verification...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!pending) return null;

  return (
    <div className="admin-login-page">
      <div className="ff-hero-ingredients">
        <div className="ff-ingredient tomato"></div>
        <div className="ff-ingredient pepper"></div>
        <div className="ff-ingredient mushroom"></div>
        <div className="ff-ingredient leaf"></div>
        <div className="ff-ingredient onion"></div>
        <div className="ff-ingredient carrot"></div>
        <div className="ff-ingredient garlic"></div>
        <div className="ff-ingredient broccoli"></div>
        <div className="ff-ingredient banana"></div>
        <div className="ff-ingredient bread"></div>
        <div className="ff-ingredient egg"></div>
        <div className="ff-ingredient sausage"></div>
        <div className="ff-ingredient cheese"></div>
        <div className="ff-ingredient lemon"></div>
        <div className="ff-ingredient strawberry"></div>
      </div>

      <div className="login-box">
        <div className="card card-primary card-outline">
          <div className="card-body login-card-body otp-card-body">
            <div className="text-center mb-4">
              <img src="/fodyfee_logo.png" alt="FodyFee" style={{ width: '80px', height: '80px' }} />
              <h3 className="login-heading">Verify Your Account</h3>
              <p className="login-subheading">Enter the OTP code to continue sign-up</p>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            <div className="otp-methods">
              <button
                type="button"
                className={`otp-method-btn ${method === 'email' ? 'active' : ''}`}
                onClick={() => { setMethod('email'); setSessionId(''); setMaskedTarget(''); setCodeDigits(Array(6).fill('')); setError(''); }}
                disabled={sending || verifying}
              >
                <span className="otp-method-title">Email</span>
                <span className="otp-method-subtitle">{pending.email}</span>
              </button>

              {canUseSms && (
                <button
                  type="button"
                  className={`otp-method-btn ${method === 'sms' ? 'active' : ''}`}
                  onClick={() => { setMethod('sms'); setSessionId(''); setMaskedTarget(''); setCodeDigits(Array(6).fill('')); setError(''); }}
                  disabled={sending || verifying}
                >
                  <span className="otp-method-title">Text Message</span>
                  <span className="otp-method-subtitle">{maskPhoneDisplay(pending.phone || '')}</span>
                </button>
              )}
            </div>

            {!sessionId && (
              <div className="row mb-2">
                <div className="col-12">
                  <button
                    type="button"
                    className="btn btn-primary btn-block"
                    onClick={() => sendOtp(method)}
                    disabled={sending || verifying}
                  >
                    {sending ? 'Sending...' : `Send code to my ${method === 'sms' ? 'phone (SMS)' : 'email'}`}
                  </button>
                </div>
              </div>
            )}

            {sessionId && (
            <div className="otp-destination">
              Code sent to <strong>{maskedTarget || (method === 'sms' ? maskPhoneDisplay(pending.phone || '') : pending.email)}</strong>
            </div>
            )}

            {sessionId && (
            <div className="otp-input-row">
              {codeDigits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  className="otp-digit-input"
                  value={digit}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  disabled={sending || verifying}
                />
              ))}
            </div>
            )}

            {sessionId && (
            <div className="otp-actions">
              <button
                type="button"
                className="btn btn-link"
                onClick={() => sendOtp(method)}
                disabled={sending || verifying || resendSeconds > 0}
              >
                {sending ? 'Sending...' : resendSeconds > 0 ? `Resend in ${resendSeconds}s` : 'Resend code'}
              </button>
            </div>
            )}

            {sessionId && (
            <div className="row">
              <div className="col-12">
                <button
                  type="button"
                  className="btn btn-primary btn-block"
                  onClick={handleVerify}
                  disabled={verifying || sending}
                >
                  {verifying ? 'Verifying...' : 'Verify and Continue'}
                </button>
              </div>
            </div>
            )}

            <div className="row mt-3">
              <div className="col-12 text-center">
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={() => {
                    clearPendingSignup();
                    navigate(`/admin/signup${location.search || ''}`);
                  }}
                >
                  Back to Sign Up
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSignupOtpPage;
